import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadLocalConfig, loadRound } from "../../state/persistence";
import { isRouteReady, preloadRoute } from "../routes";
import { transitionTo, type NavOptions } from "./transitionStore";
import { transitionKindFor, type TransitionRequest } from "./timing";

/**
 * The longest a tap waits for its screen's code before the slide starts anyway.
 *
 * Normally zero: `warmRoutes` has every chunk in within seconds of launch. This
 * covers a tap that beats it - on a slow connection, or straight off the splash.
 * Long enough for a chunk on mobile data; short enough that a tap never feels
 * ignored. Past it the slide plays over Suspense, which is what it always did.
 */
const WAIT_FOR_SCREEN_MS = 450;

/** Bumped on every navigation, so a wait that is overtaken by a newer tap is dropped. */
let latestTap = 0;

export interface TransitionNavigateOptions extends TransitionRequest, NavOptions {
  /** Skip the transition entirely — a redirect the player did not ask for. */
  instant?: boolean;
}

/**
 * A drop-in replacement for `useNavigate` that plays a screen transition over
 * the route change.
 *
 * Deliberately the same shape as the hook it replaces, so adopting it is one
 * word per screen rather than a rewrite of forty call sites — and so a screen
 * that wants a plain navigation can pass `{ instant: true }` instead of reaching
 * for a second hook.
 *
 * The transition kind comes from the destination (`transitionKindFor`), so no
 * call site has to remember that the table gets the long one. What a call site
 * CAN add is context the route does not carry: `back: true` on a retreat, which
 * turns the minor transition round, and `hasTurnClock` where only that screen
 * knows a clock is ticking.
 */
export function useTransitionNavigate() {
  const navigate = useNavigate();
  // Where we are travelling FROM, which is half of what decides which way the
  // screens slide (see `slideFor`).
  const from = useLocation().pathname;
  return useCallback(
    (to: string | number, opts: TransitionNavigateOptions = {}) => {
      // `nav(-1)` and friends: history moves have no destination to inspect and
      // are rare in this app, so they go straight through.
      if (typeof to === "number") {
        navigate(to);
        return;
      }
      if (opts.instant) {
        navigate(to, { replace: opts.replace, state: opts.state });
        return;
      }
      const req = { ...opts, from, hasTurnClock: opts.hasTurnClock ?? destinationHasTurnClock(to) };
      const tap = ++latestTap;
      const loading = preloadRoute(to);
      // A MAJOR transition covers the screen while the route swaps, so a chunk
      // still in flight is hidden by the cards - that is what they are for. A
      // slide covers nothing: the new screen is on show from its first frame, so
      // it is started once the screen can render without suspending.
      if (isRouteReady(to) || transitionKindFor(to, req) === "major") {
        transitionTo(to, navigate, req);
        return;
      }
      let started = false;
      const go = () => {
        if (started || tap !== latestTap) return;
        started = true;
        transitionTo(to, navigate, req);
      };
      const timer = window.setTimeout(go, WAIT_FOR_SCREEN_MS);
      void loading.then(() => {
        window.clearTimeout(timer);
        go();
      });
    },
    [navigate, from],
  );
}

/**
 * Is a turn clock already running at the table this navigation leads to?
 *
 * Only answerable here for LOCAL play, where the config that dealt the round is
 * on this device: the setup screen writes it on every edit, so it is current by
 * the time anything navigates, and a resumed round carries its own. Online, the
 * table's timer is on a document this screen may not have read yet — so the
 * lobby passes `hasTurnClock` explicitly, and that overrides this.
 *
 * A wrong answer costs a slightly long transition, never a broken one.
 */
function destinationHasTurnClock(to: string): boolean {
  if (!to.startsWith("/local/play")) return false;
  try {
    const resumed = loadRound()?.config.turnTimerSeconds;
    if (resumed != null) return true;
    return loadLocalConfig().turnTimerSeconds != null;
  } catch {
    return false;
  }
}
